import { dedent } from 'ts-dedent'
import {
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { removeColumns } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class DropColumn extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.dropcolumn',
        group: 'Data Preparation',
        title: 'Drop Columns',
        description: `
      This component generates a new table with the same content as the input one, except for the selected columns.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Columns to drop',
          parent: 'source',
          mode: WorkflowComponentParameterMode.Multiple,
          noDefault: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''

    const columns = Array.isArray(preparedParams.column) ? preparedParams.column : [preparedParams.column]
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `SELECT * EXCEPT (${columns.map((c) => '`' + c + '`').join(', ')})
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.DatabricksRest: {
        const select = `SELECT /*+ REPARTITION(40) */ * EXCEPT (${columns.join(', ')})
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        const select = `SELECT * EXCLUDE (${columns.join(', ')})
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      default: {
        const select = `SELECT *
                      FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        for (const column of columns) {
          query += dedent`
          ALTER TABLE ${outputTablename}
          DROP COLUMN ${`"${column}"`};`
        }
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const preparedParams = this._prepareParameters(params)
    const columnsToRemove = Array.isArray(preparedParams.column) ? preparedParams.column : [preparedParams.column]
    return removeColumns(columns.source, columnsToRemove as Array<string>)
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { DropColumn }
